import { Form } from "antd";
import { useEffect } from "react";
import { FormModal } from "@/shared/components/FormModal";
import { useCreateTaskType, useUpdateTaskType } from "../hooks/useTaskTypes";
import { useTaskTypesStore } from "../store/task-types.store";
import type { CreateTaskTypeRequest } from "../types";
import { TaskTypeForm } from "./TaskTypeForm";

export function TaskTypeFormModal() {
	const [form] = Form.useForm<CreateTaskTypeRequest>();
	const { modalOpen, editingItem, closeModal } = useTaskTypesStore();

	const { mutate: create, isPending: isCreating } = useCreateTaskType();
	const { mutate: update, isPending: isUpdating } = useUpdateTaskType();

	const close = () => {
		form.resetFields();
		closeModal();
	};

	useEffect(() => {
		if (modalOpen) {
			if (editingItem) {
				form.setFieldsValue({
					name: editingItem.name,
					nameUz: editingItem.nameUz,
					nameRu: editingItem.nameRu ?? "",
					nameEn: editingItem.nameEn ?? "",
					code: editingItem.code,
					category: editingItem.category ?? "OTHER",
					color: editingItem.color ?? "",
					description: editingItem.description ?? "",
					defaultPriority: editingItem.defaultPriority ?? "MEDIUM",
					sortOrder: editingItem.sortOrder,
					isActive: editingItem.isActive,
				});
			} else {
				form.resetFields();
			}
		}
	}, [modalOpen, editingItem, form]);

	const buildUpdateData = (values: CreateTaskTypeRequest): Partial<CreateTaskTypeRequest> => {
		if (!editingItem) {
			return {};
		}
		const current: Partial<CreateTaskTypeRequest> = {
			name: editingItem.name,
			category: editingItem.category,
			description: editingItem.description ?? "",
			defaultPriority: editingItem.defaultPriority ?? "MEDIUM",
			sortOrder: editingItem.sortOrder,
			isActive: editingItem.isActive,
		};
		const next: Partial<CreateTaskTypeRequest> = {
			name: values.name,
			category: values.category,
			description: values.description || undefined,
			defaultPriority: values.defaultPriority,
			sortOrder: values.sortOrder,
			isActive: values.isActive,
		};
		const d: Partial<CreateTaskTypeRequest> = {};
		for (const key of Object.keys(next) as (keyof CreateTaskTypeRequest)[]) {
			const raw = key === "description" ? (values.description ?? "") : next[key];
			if (raw !== current[key]) {
				Object.assign(d, { [key]: next[key] });
			}
		}
		return d;
	};

	const buildCreateData = (values: CreateTaskTypeRequest): CreateTaskTypeRequest => ({
		name: values.name,
		nameUz: values.nameUz ?? values.name,
		code: values.code ?? (values.name ? values.name.toUpperCase().replace(/\s+/g, "_") : "TASK"),
		category: values.category ?? "OTHER",
		nameRu: values.nameRu || undefined,
		nameEn: values.nameEn || undefined,
		color: values.color || undefined,
		description: values.description || undefined,
		defaultPriority: values.defaultPriority ?? "MEDIUM",
		sortOrder: values.sortOrder ?? 0,
		isActive: values.isActive ?? true,
	});

	const onFinish = (values: CreateTaskTypeRequest) => {
		if (editingItem) {
			update({ id: editingItem.id, data: buildUpdateData(values) }, { onSuccess: close });
		} else {
			create(buildCreateData(values), { onSuccess: close });
		}
	};

	return (
		<FormModal
			open={modalOpen}
			editing={!!editingItem}
			title="Yangi topshiriq turi"
			editTitle="Topshiriq turini tahrirlash"
			onCancel={close}
			onOk={form.submit}
			confirmLoading={isCreating || isUpdating}
			width={720}
		>
			<Form
				form={form}
				layout="vertical"
				requiredMark={false}
				onFinish={onFinish}
				initialValues={{
					category: "OTHER",
					defaultPriority: "MEDIUM",
					sortOrder: 0,
					isActive: true,
				}}
			>
				<TaskTypeForm />
			</Form>
		</FormModal>
	);
}
